import { css } from 'lit';
import { hostReset, floatingPanel, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  floatingPanel('.dp-panel', { durationVar: '--uwc-dp-duration', durationDefault: '160ms' }),
  placementOrigins,
  css`
    /* ── Layer 1: Host layout + fluid ────────────────────────────────────────── */
    :host {
      display:     inline-block;
      position:    relative;
      font-family: var(--uwc-font-family, inherit);
      font-size:   var(--uwc-dp-font-size, ${t.fontSizeMd});
    }
    :host([inline]) { display: block; }
    :host([fluid])  { display: block; width: 100%; }

    .dp-root {
      display:  inline-flex;
      position: relative;
    }
    :host([fluid]) .dp-root { display: flex; width: 100%; }

    /* ── Layer 2: Trigger wrapper ─────────────────────────────────────────────── */
    .dp-trigger {
      display:       inline-flex;
      align-items:   center;
      background:    var(--uwc-dp-trigger-bg,     ${t.surface});
      border:        var(--uwc-dp-trigger-border, 1px solid ${t.borderControl});
      border-radius: var(--uwc-dp-trigger-radius, ${t.radiusMd});
      min-width:     200px;
      min-height:    var(--uwc-dp-trigger-height, ${t.controlHeightMd});
      overflow:      hidden;
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
      cursor:        pointer;
    }
    :host([fluid]) .dp-trigger { width: 100%; }

    .dp-trigger:focus-within,
    .dp-trigger--open {
      border-color: var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary}));
      box-shadow:   0 0 0 3px color-mix(in oklab, var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary})) 20%, transparent);
      outline:      none;
    }

    .dp-input {
      flex:          1;
      min-width:     0;
      padding:       ${t.space2} 10px;
      border:        none;
      outline:       none;
      font-family:   var(--uwc-font-family, inherit);
      font-size:     var(--uwc-dp-trigger-font-size, inherit);
      color:         var(--uwc-dp-trigger-color, ${t.text});
      background:    transparent;
      cursor:        pointer;
      white-space:   nowrap;
      overflow:      hidden;
      text-overflow: ellipsis;
    }
    .dp-input::placeholder { color: var(--uwc-dp-placeholder-color, ${t.textMuted}); }

    .dp-icon--btn {
      flex-shrink:     0;
      display:         flex;
      align-items:     center;
      justify-content: center;
      border:          none;
      background:      transparent;
      cursor:          pointer;
      padding:         0 ${t.space2};
      align-self:      stretch;
      color:           var(--uwc-dp-icon-color, ${t.textMuted});
      transition:      color ${t.durationFast};
      border-radius:   0;
    }
    .dp-icon--btn:hover  { color: var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary})); }
    .dp-icon--btn:disabled,
    .dp-icon--btn[disabled] { opacity: 0.4; cursor: not-allowed; pointer-events: none; }

    /* ── Layer 3: Panel base ──────────────────────────────────────────────────── */
    .dp-panel {
      background:     var(--uwc-dp-bg,     ${t.surface});
      border:         var(--uwc-dp-border, 1px solid ${t.border});
      border-radius:  var(--uwc-dp-radius, ${t.radiusMd});
      box-shadow:     var(--uwc-dp-shadow, ${t.shadowLg});
      z-index:        var(--uwc-dp-z,      ${t.zFloat});
      overflow:       hidden;
      user-select:    none;
      pointer-events: none;
    }
    .dp-panel.is-open { pointer-events: auto; }

    /* ── Layer 4: Calendar table layout ──────────────────────────────────────── */
    .dp-months { display: flex; }

    :host([fluid]) .dp-months { width: 100%; }
    :host([fluid]) .dp-cal    { flex: 1; }

    .dp-cal {
      display:        flex;
      flex-direction: column;
      gap:            ${t.space2};
      padding:        ${t.space4};
      /* Calendar font + sizing are derived from the global font token so cells
         (sized in em below) scale with the size presets. */
      font-size:      var(--uwc-dp-font-size, ${t.fontSizeMd});
      min-width:      19em;
    }
    .dp-cal + .dp-cal { border-left: 1px solid ${t.borderSubtle}; }

    .dp-header {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      gap:             ${t.space1};
      margin-bottom:   ${t.space1};
    }

    .dp-nav-btn {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      font-size:       var(--uwc-dp-font-size, ${t.fontSizeMd});
      width:           2.35em;
      height:          2.35em;
      border:          none;
      background:      none;
      border-radius:   ${t.radiusFull};
      cursor:          pointer;
      color:           var(--uwc-dp-nav-color, ${t.textSecondary});
      flex-shrink:     0;
      transition:      background ${t.durationFast}, color ${t.durationFast};
    }
    .dp-nav-btn:hover  { background: ${t.hoverBg}; color: ${t.text}; }
    .dp-nav-btn[hidden] { visibility: hidden; pointer-events: none; }

    .dp-header-title {
      display:         flex;
      align-items:     center;
      gap:             ${t.space1};
      flex:            1;
      justify-content: center;
    }

    .dp-month-label,
    .dp-year-label {
      font-size:     var(--uwc-dp-header-font-size, inherit);
      font-weight:   ${t.fontWeightSemibold};
      color:         var(--uwc-dp-header-color, ${t.text});
      background:    none;
      border:        none;
      cursor:        pointer;
      padding:       ${t.space1} ${t.space2};
      border-radius: ${t.radiusSm};
      white-space:   nowrap;
      transition:    background ${t.durationFast};
      line-height:   1;
    }
    .dp-month-label:hover,
    .dp-year-label:hover { background: ${t.hoverBg}; }

    .dp-weekdays {
      display:               grid;
      grid-template-columns: repeat(7, 1fr);
      gap:                   2px;
    }
    .dp-weekday {
      text-align:  center;
      font-size:   ${t.fontSizeMd};
      font-weight: ${t.fontWeightSemibold};
      color:       ${t.textSecondary};
      padding:     ${t.space1} 0;
    }

    .dp-weeks { display: flex; flex-direction: column; gap: 2px; }
    .dp-week  { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }

    /* ── Layer 5: Cell states ─────────────────────────────────────────────────── */
    .dp-day {
      position:        relative;
      display:         flex;
      align-items:     center;
      justify-content: center;
      height:          2.3em;
      width:           100%;
      border-radius:   var(--uwc-dp-cell-radius, ${t.radiusFull});
      font-size:       var(--uwc-dp-cell-font-size, inherit);
      color:           var(--uwc-dp-cell-color, ${t.text});
      background:      transparent;
      border:          none;
      cursor:          pointer;
      transition:      background ${t.durationFast}, color ${t.durationFast};
      padding:         0;
      line-height:     1;
    }
    .dp-day:hover:not(.dp-day--disabled):not(.dp-day--other-month) {
      background:    ${t.hoverBg};
      border-radius: ${t.radiusFull};
    }
    .dp-day:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
      z-index:        1;
    }

    .dp-day--today:not(.dp-day--selected):not(.dp-day--range-start):not(.dp-day--range-end) {
      background:    ${t.hoverBg};
      border-radius: ${t.radiusFull};
      color:         var(--uwc-dp-today-color, var(--uwc-color-primary, ${t.primary}));
      font-weight:   ${t.fontWeightSemibold};
    }

    .dp-day--selected {
      background:    var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary}));
      color:         var(--uwc-dp-selected-color, #fff);
      font-weight:   ${t.fontWeightSemibold};
      border-radius: ${t.radiusFull};
    }
    .dp-day.dp-day--selected:hover:not(.dp-day--disabled) {
      background: color-mix(in oklab, var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary})) 88%, black);
      color:      var(--uwc-dp-selected-color, #fff);
    }

    .dp-day--range-start,
    .dp-day--range-end {
      background:    var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary}));
      color:         var(--uwc-dp-selected-color, #fff);
      font-weight:   ${t.fontWeightSemibold};
      border-radius: ${t.radiusFull};
      position:      relative;
      z-index:       1;
    }
    .dp-day.dp-day--range-start:hover:not(.dp-day--disabled),
    .dp-day.dp-day--range-end:hover:not(.dp-day--disabled) {
      background: color-mix(in oklab, var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary})) 88%, black);
      color:      var(--uwc-dp-selected-color, #fff);
    }

    .dp-day--in-range,
    .dp-day--hover-range {
      background:    var(--uwc-dp-range-bg, rgba(99,102,241,0.10));
      border-radius: 0;
    }

    .dp-day--other-month {
      color:   ${t.textMuted};
      opacity: 0.45;
    }

    .dp-day--disabled {
      color:          ${t.textDisabled};
      cursor:         not-allowed;
      pointer-events: none;
    }

    /* ── Layer 6: Month picker 3×4 grid ──────────────────────────────────────── */
    .dp-month-grid {
      display:               grid;
      grid-template-columns: repeat(3, 1fr);
      gap:                   4px;
      padding:               ${t.space1} 0;
    }

    /* ── Layer 7: Year picker decade grid ────────────────────────────────────── */
    .dp-year-grid {
      display:               grid;
      grid-template-columns: repeat(2, 1fr);
      gap:                   4px;
      padding:               ${t.space1} 0;
    }

    .dp-picker-cell {
      display:         flex;
      align-items:     center;
      justify-content: center;
      height:          2.35em;
      width:           100%;
      border:          none;
      background:      transparent;
      border-radius:   var(--uwc-dp-cell-radius, ${t.radiusMd});
      font-size:       var(--uwc-dp-cell-font-size, ${t.fontSizeMd});
      color:           var(--uwc-dp-cell-color, ${t.text});
      cursor:          pointer;
      transition:      background ${t.durationFast}, color ${t.durationFast};
      padding:         0;
      line-height:     1;
    }
    .dp-picker-cell:hover:not(.dp-picker-cell--disabled) { background: ${t.hoverBg}; }
    .dp-picker-cell:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
      z-index:        1;
    }
    .dp-picker-cell--current:not(.dp-picker-cell--selected) {
      color:       var(--uwc-dp-today-color, var(--uwc-color-primary, ${t.primary}));
      font-weight: ${t.fontWeightSemibold};
      background:  ${t.hoverBg};
    }
    .dp-picker-cell--selected {
      background:    var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary}));
      color:         var(--uwc-dp-selected-color, #fff);
      font-weight:   ${t.fontWeightSemibold};
      border-radius: ${t.radiusMd};
    }
    .dp-picker-cell.dp-picker-cell--selected:hover:not(.dp-picker-cell--disabled) {
      background: color-mix(in oklab, var(--uwc-dp-selected-bg, var(--uwc-color-primary, ${t.primary})) 88%, black);
      color:      var(--uwc-dp-selected-color, #fff);
    }
    .dp-picker-cell--disabled {
      color:          ${t.textDisabled};
      cursor:         not-allowed;
      pointer-events: none;
    }

    /* ── Layer 8: Time picker spinner columns ─────────────────────────────────── */
    .dp-time {
      display:         flex;
      align-items:     center;
      justify-content: center;
      gap:             ${t.space3};
      padding:         ${t.space3} ${t.space4};
      border-top:      1px solid ${t.borderSubtle};
    }
    :host([time-only]) .dp-time { border-top: none; padding: ${t.space4}; }

    .dp-time-col {
      display:        flex;
      flex-direction: column;
      align-items:    center;
      gap:            ${t.space1};
    }

    .dp-time-btn {
      display:         flex;
      align-items:     center;
      justify-content: center;
      width:           28px;
      height:          28px;
      border:          none;
      background:      none;
      border-radius:   ${t.radiusFull};
      cursor:          pointer;
      color:           ${t.textSecondary};
      transition:      background ${t.durationFast}, color ${t.durationFast};
      padding:         0;
    }
    .dp-time-btn:hover { background: ${t.hoverBg}; color: ${t.text}; }
    .dp-time-btn:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }

    .dp-time-val {
      min-width:            2ch;
      text-align:           center;
      font-size:            ${t.fontSizeLg};
      font-weight:          ${t.fontWeightSemibold};
      color:                ${t.text};
      line-height:          1;
      font-variant-numeric: tabular-nums;
    }

    .dp-time-sep {
      font-size:   ${t.fontSizeLg};
      font-weight: ${t.fontWeightSemibold};
      color:       ${t.textSecondary};
      user-select: none;
      padding-top: 4px;
    }

    .dp-time-ampm {
      padding:       ${t.space1} ${t.space2};
      border:        1px solid ${t.borderControl};
      border-radius: ${t.radiusMd};
      background:    transparent;
      color:         ${t.text};
      font-size:     ${t.fontSizeSm};
      font-weight:   ${t.fontWeightSemibold};
      cursor:        pointer;
      transition:    background ${t.durationFast};
      align-self:    center;
      margin-top:    4px;
    }
    .dp-time-ampm:hover { background: ${t.hoverBg}; }
    .dp-time-ampm:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }

    /* ── Layer 9: Button bar (Today / Clear) ──────────────────────────────────── */
    .dp-button-bar {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      gap:             ${t.space2};
      padding:         ${t.space2} ${t.space3};
      border-top:      1px solid ${t.borderSubtle};
    }

    /* ── Layer 10: Size variants ──────────────────────────────────────────────── */
    :host([size=small]) { font-size: ${t.fontSizeSm}; }
    :host([size=small]) .dp-trigger     { min-width: 160px; }
    :host([size=small]) .dp-input       { padding: 4px 8px; font-size: ${t.fontSizeSm}; }
    :host([size=small]) .dp-icon--btn   { height: 28px; }
    :host([size=small]) .dp-cal         { min-width: 220px; padding: ${t.space3}; gap: ${t.space1}; }
    :host([size=small]) .dp-day         { height: 26px; font-size: ${t.fontSizeSm}; }
    :host([size=small]) .dp-weekday     { font-size: ${t.fontSizeXs}; padding: 2px 0; }
    :host([size=small]) .dp-picker-cell { height: 28px; font-size: ${t.fontSizeSm}; }
    :host([size=small]) .dp-nav-btn     { width: 26px; height: 26px; }
    :host([size=small]) .dp-month-label,
    :host([size=small]) .dp-year-label  { font-size: ${t.fontSizeSm}; }
    :host([size=small]) .dp-time-val    { font-size: ${t.fontSizeMd}; }
    :host([size=small]) .dp-time-btn    { width: 24px; height: 24px; }

    :host([size=large]) { font-size: ${t.fontSizeLg}; }
    :host([size=large]) .dp-trigger     { min-width: 240px; }
    :host([size=large]) .dp-input       { padding: 10px 14px; font-size: ${t.fontSizeLg}; }
    :host([size=large]) .dp-icon--btn   { height: 42px; }
    :host([size=large]) .dp-cal         { min-width: 308px; padding: ${t.space5}; }
    :host([size=large]) .dp-day         { height: 38px; }
    :host([size=large]) .dp-picker-cell { height: 40px; font-size: ${t.fontSizeLg}; }
    :host([size=large]) .dp-nav-btn     { width: 38px; height: 38px; }
    :host([size=large]) .dp-month-label,
    :host([size=large]) .dp-year-label  { font-size: ${t.fontSizeLg}; }
    :host([size=large]) .dp-time-val    { font-size: ${t.fontSizeXl}; }
    :host([size=large]) .dp-time-btn    { width: 32px; height: 32px; }

    /* ── Layer 11: Appearance – filled variant ────────────────────────────────── */
    :host([variant=filled]) .dp-trigger {
      background:   var(--uwc-dp-trigger-fill-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    :host([variant=filled]) .dp-trigger:focus-within,
    :host([variant=filled]) .dp-trigger.dp-trigger--open {
      border-color: var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary}));
      background:   color-mix(in oklab, var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary})) 6%, ${t.surfaceRaised});
      box-shadow:   none;
    }

    /* ── Layer 12: Inline mode ────────────────────────────────────────────────── */
    :host([inline]) .dp-panel {
      position:       static !important;
      opacity:        1 !important;
      transform:      none !important;
      pointer-events: auto;
      display:        inline-block;
      box-shadow:     none;
      border:         1px solid ${t.borderControl};
    }
    :host([inline]) .dp-root           { display: none; }
    :host([fluid][inline]) .dp-panel   { display: block; width: 100%; }

    /* ── Layer 13: Float label ────────────────────────────────────────────────── */
    .dp-float-label {
      position:       absolute;
      left:           calc(10px + 1px);
      top:            50%;
      transform:      translateY(-50%);
      font-size:      ${t.fontSizeSm};
      color:          ${t.textMuted};
      pointer-events: none;
      line-height:    1;
      padding-inline: 0.125rem;
      white-space:    nowrap;
      transition:
        top       0.18s ease,
        transform 0.18s ease,
        font-size 0.18s ease,
        color     0.18s ease;
    }

    :host([float-label="over"]) .dp-root {
      margin-top: 1.5rem;
      overflow:   visible;
    }
    :host([float-label="over"]) .dp-float-label.dp-float-label--active {
      top:       0;
      transform: translateY(calc(-100% - 0.25rem));
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary}));
    }
    :host([float-label="over"]) .dp-trigger--empty .dp-input::placeholder {
      color: transparent;
    }

    :host([float-label="in"]) .dp-trigger {
      padding-top:    1.125rem;
      min-height:     2.625rem;
      align-items:    flex-end;
      padding-bottom: 0.375rem;
    }
    :host([float-label="in"]) .dp-float-label.dp-float-label--active {
      top:       0.3125rem;
      transform: none;
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary}));
    }
    :host([float-label="in"]) .dp-trigger--empty .dp-input::placeholder {
      color: transparent;
    }

    :host([float-label="on"]) .dp-root {
      margin-top: 0.75rem;
      overflow:   visible;
    }
    :host([float-label="on"]) .dp-float-label {
      background: var(--uwc-dp-trigger-bg, ${t.surface});
      padding:    0 4px;
    }
    :host([float-label="on"]) .dp-float-label.dp-float-label--active {
      top:       0;
      transform: translateY(-50%);
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-dp-primary, var(--uwc-color-primary, ${t.primary}));
    }
    :host([float-label="on"]) .dp-trigger--empty .dp-input::placeholder {
      color: transparent;
    }

    @media (prefers-reduced-motion: reduce) {
      .dp-float-label { transition: none; }
    }

    /* ── Layer 14: Disabled / invalid / readonly ──────────────────────────────── */
    :host([disabled]) .dp-trigger { opacity: 0.5; pointer-events: none; }
    :host([disabled]) .dp-panel   { pointer-events: none; opacity: 0.6; }
    :host([readonly]) .dp-input   { cursor: default; }
    :host([readonly]) .dp-day     { pointer-events: none; }

    :host([invalid]) .dp-trigger {
      border-color: var(--uwc-dp-danger, ${t.danger});
    }
    :host([invalid]) .dp-trigger:focus-within,
    :host([invalid]) .dp-trigger.dp-trigger--open {
      border-color: var(--uwc-dp-danger, ${t.danger});
      box-shadow:   0 0 0 3px color-mix(in oklab, ${t.danger} 18%, transparent);
    }
  `,
];
